import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, CheckCircle, TrendingUp, Activity, Users, DollarSign, Loader2, Shield } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api, { formatZARFull } from '@/lib/api';
import { toast } from 'sonner';

const SEVERITY = { high: 'bg-red-50 text-red-700 border-red-200', medium: 'bg-amber-50 text-amber-700 border-amber-200' };
const CHART_COLORS = ['#CBA871', '#1A1A1A', '#52525B', '#A1A1AA', '#B8860B', '#3B82F6'];

export default function PracticeIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/studio/intelligence');
      setData(d);
    } catch {
      setData(null);
    }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-dark/30" /></div>;
  if (!data) return (
    <div className="text-center py-16">
      <Activity className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
      <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">Intelligence requires data</h3>
      <p className="text-brand-dark/40 font-body text-sm">Convert leads into active projects to populate practice insights.</p>
    </div>
  );

  const { health, scope_intelligence, fee_performance, client_quality } = data;

  return (
    <div className="space-y-8" data-testid="practice-intelligence">

      {/* ===== B1: PROJECT HEALTH ===== */}
      <section data-testid="health-section">
        <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Practice Health
        </h3>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Active', value: health.total_active, color: 'text-brand-dark' },
            { label: 'Completed', value: health.total_completed, color: 'text-green-600' },
            { label: 'On Hold', value: health.total_on_hold, color: 'text-amber-600' },
            { label: 'Attention', value: health.attention_projects?.length || 0, color: health.attention_projects?.length ? 'text-red-600' : 'text-brand-dark/30' },
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-sm border border-black/10 p-4" data-testid={`health-${c.label.toLowerCase()}`}>
              <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">{c.label}</p>
              <p className={`font-heading text-2xl font-medium ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Stage distribution */}
        {health.stage_distribution?.length > 0 && (
          <div className="bg-white rounded-sm border border-black/10 p-5 mb-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-3">Projects by Current Stage</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={health.stage_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="stage" fontSize={10} angle={-15} textAnchor="end" height={50} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#CBA871" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Attention flags */}
        {health.attention_projects?.length > 0 && (
          <div className="bg-white rounded-sm border border-red-100 p-5" data-testid="attention-projects">
            <p className="text-xs font-heading uppercase tracking-wider text-red-600/70 mb-3 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Projects Needing Attention
            </p>
            <div className="space-y-3">
              {health.attention_projects.map((ap) => (
                <div key={ap.project_id} className="border-l-2 border-red-300 pl-4">
                  <p className="text-sm font-body font-medium">{ap.name}</p>
                  <p className="text-xs text-brand-dark/40 font-body">{ap.client_name}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {ap.flags.map((f, i) => (
                      <Badge key={i} variant="outline" className={`text-xs ${SEVERITY[f.severity]}`}>{f.message}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {health.attention_projects?.length === 0 && health.total_active > 0 && (
          <div className="flex items-center gap-2 p-4 bg-green-50 rounded-sm border border-green-100">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-700 font-body">All active projects are healthy. No flags detected.</p>
          </div>
        )}
      </section>

      <Separator />

      {/* ===== B2: SCOPE CREEP INTELLIGENCE ===== */}
      <section data-testid="scope-section">
        <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Scope Intelligence
        </h3>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Total Changes</p>
            <p className="font-heading text-2xl font-medium text-brand-dark">{scope_intelligence.total_changes}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Cost Impacts</p>
            <p className="font-heading text-2xl font-medium text-red-600">{scope_intelligence.cost_impacts}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Time Impacts</p>
            <p className="font-heading text-2xl font-medium text-amber-600">{scope_intelligence.time_impacts}</p>
          </div>
        </div>

        {scope_intelligence.by_project?.length > 0 ? (
          <div className="bg-white rounded-sm border border-black/10 p-5">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-3">Scope Changes by Project</p>
            <ResponsiveContainer width="100%" height={Math.max(120, scope_intelligence.by_project.length * 40)}>
              <BarChart data={scope_intelligence.by_project} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis type="number" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" fontSize={11} width={140} />
                <Tooltip />
                <Bar dataKey="cost_impacts" stackId="a" fill="#ef4444" name="Cost" radius={0} />
                <Bar dataKey="time_impacts" stackId="a" fill="#f59e0b" name="Time" radius={0} />
                <Bar dataKey="no_impacts" stackId="a" fill="#d1d5db" name="None" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
            <p className="text-brand-dark/30 font-body text-sm">No scope changes recorded across projects.</p>
          </div>
        )}
      </section>

      <Separator />

      {/* ===== B3: FEE & TIME PERFORMANCE ===== */}
      <section data-testid="fee-section">
        <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
          <DollarSign className="w-3.5 h-3.5" /> Fee & Time Performance
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Total Fees</p>
            <p className="font-heading text-lg font-medium text-brand-dark">{formatZARFull(fee_performance.total_fees)}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Invoiced</p>
            <p className="font-heading text-lg font-medium text-blue-600">{formatZARFull(fee_performance.total_invoiced)}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Collected</p>
            <p className="font-heading text-lg font-medium text-green-600">{formatZARFull(fee_performance.total_paid)}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Outstanding</p>
            <p className="font-heading text-lg font-medium text-amber-600">{formatZARFull(fee_performance.total_outstanding)}</p>
          </div>
          <div className="bg-white rounded-sm border border-black/10 p-4">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Recovery Rate</p>
            <p className="font-heading text-lg font-medium text-brand-dark">{fee_performance.recovery_rate}%</p>
          </div>
        </div>

        {/* Per-project fee summary */}
        {fee_performance.by_project?.length > 0 && (
          <div className="bg-white rounded-sm border border-black/10 p-5">
            <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-3">Project Fee Overview</p>
            <div className="space-y-2">
              {fee_performance.by_project.map((fp) => {
                const progressPct = fp.total_stages > 0 ? Math.round((fp.stages_completed / fp.total_stages) * 100) : 0;
                const recoveryPct = fp.total_fee > 0 ? Math.round((fp.paid / fp.total_fee) * 100) : 0;
                const drifting = progressPct > 30 && recoveryPct < 15 && fp.total_fee > 0;
                return (
                  <div key={fp.project_id} className={`p-3 rounded-sm border ${drifting ? 'border-red-200 bg-red-50/30' : 'border-black/5'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-body font-medium">{fp.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">{fp.status}</Badge>
                        {drifting && <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">Fee Drift</Badge>}
                      </div>
                      <span className="text-sm font-body font-medium">{fp.total_fee > 0 ? formatZARFull(fp.total_fee) : 'No fee'}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-brand-dark/40 font-body">
                      <span>Stage: {fp.stages_completed}/{fp.total_stages} ({progressPct}%)</span>
                      <span>Collected: {recoveryPct}%</span>
                      {fp.stage_durations?.filter(s => s.status === 'in_progress').map(s => (
                        <span key={s.stage} className={s.days > 60 ? 'text-red-500' : ''}>{s.stage}: {s.days}d</span>
                      ))}
                    </div>
                    {/* Twin progress bars */}
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-brand-dark/30 w-16">Progress</span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-accent rounded-full" style={{ width: `${progressPct}%` }} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-brand-dark/30 w-16">Recovery</span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full" style={{ width: `${recoveryPct}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <Separator />

      {/* ===== B4: CLIENT QUALITY INDEX ===== */}
      <section data-testid="quality-section">
        <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Client Quality Index
        </h3>

        {client_quality?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Source comparison chart */}
            <div className="bg-white rounded-sm border border-black/10 p-5">
              <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-3">Projects by Lead Source</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={client_quality} cx="50%" cy="50%" outerRadius={65} dataKey="projects" nameKey="source"
                    label={({ source, projects }) => `${source} (${projects})`} labelLine={false}>
                    {client_quality.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Quality indicators */}
            <div className="bg-white rounded-sm border border-black/10 p-5">
              <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-3">Source Quality Indicators</p>
              <div className="space-y-3">
                {client_quality.map((cq) => (
                  <div key={cq.source} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
                    <div>
                      <p className="text-sm font-body font-medium">{cq.source}</p>
                      <p className="text-xs text-brand-dark/40 font-body">{cq.projects} project{cq.projects !== 1 ? 's' : ''} &middot; {cq.avg_scope_per_project} changes/project</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cq.smooth_projects > 0 && <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">{cq.smooth_projects} smooth</Badge>}
                      {cq.problematic_projects > 0 && <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">{cq.problematic_projects} complex</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-black/10 p-8 text-center">
            <p className="text-brand-dark/30 font-body text-sm">Client quality patterns will appear as more projects are tracked.</p>
          </div>
        )}
      </section>

      {/* Footer note */}
      <div className="flex items-start gap-2 p-4 bg-brand-light/50 rounded-sm text-xs text-brand-dark/30 font-body">
        <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>Practice intelligence is derived from operational data. No new data entry required. All indicators are directional, not prescriptive.</span>
      </div>
    </div>
  );
}
